import type {
    CollectionQueryConstraints,
    DatabaseAdapter
} from "@rapid/providers/database/DatabaseAdapter";
import { ActivityAudienceMode } from "@rflocal/shared/activity/types";
import type { ActivitySource, ActivityViewData } from "../types";
import { ActivityUIKind } from "../constants";
import { toDate } from "./utils";
import type { FirestoreTimestampLike } from "./types";

interface FirestoreActivityEvent {
    id: string;
    kind: string;
    entity: { type: string; id: string; url?: string };
    payload: Record<string, unknown>;
    audience: { mode: ActivityAudienceMode; targets?: string[] };
    owner: string;
    createdAt: FirestoreTimestampLike;
    updatedAt: FirestoreTimestampLike;
    ttlAt?: FirestoreTimestampLike;
}

const KNOWN_KINDS = new Set<string>(Object.values(ActivityUIKind));

/**
 * Convention-based payload mapping — backend events use ActivityViewData
 * field names directly in their payload, so no per-kind conversion needed.
 *
 * Kinds that need custom mapping (e.g. REST-backed invites) should be a
 * separate ActivitySource, not a special case here.
 */
function convertEvent(event: FirestoreActivityEvent): ActivityViewData | null {
    if (!KNOWN_KINDS.has(event.kind)) return null;
    const p = event.payload ?? {};
    return {
        id: event.id,
        kind: event.kind as ActivityUIKind,
        title: (p.title as string) || "Activity",
        description: (p.description as string) || null,
        contextLabel: (p.contextLabel as string) || undefined,
        status: (p.status as ActivityViewData["status"]) || undefined,
        icon: (p.icon as string) || undefined,
        targetUrl: (p.targetUrl as string) || event.entity?.url || null,
        openInNewTab: (p.openInNewTab as boolean) || false,
        progress: (p.progress as ActivityViewData["progress"]) || null,
        error: (p.error as string) || null,
        urgent: (p.urgent as boolean) || undefined,
        createdAt: toDate(event.createdAt),
        completedAt: event.updatedAt ? toDate(event.updatedAt) : null,
        meta: (p.meta as Record<string, unknown>) || undefined
    };
}

/**
 * ActivitySource backed by the `activity_events` Firestore collection.
 *
 * Subscribes to activity events for a workspace, filters by audience,
 * and converts each event into ActivityViewData using convention-based
 * payload mapping. No per-kind conversion logic — the backend writes
 * payload fields that match ActivityViewData field names.
 */
class ActivityEventSource implements ActivitySource {
    private workspaceId: string;
    private userId: string;
    private database: DatabaseAdapter;
    private kinds: string[];

    constructor(workspaceId: string, userId: string, database: DatabaseAdapter, kinds: string[]) {
        this.workspaceId = workspaceId;
        this.userId = userId;
        this.database = database;
        this.kinds = kinds;
    }

    subscribe(callback: (activities: ActivityViewData[]) => void): () => void {
        if (!this.workspaceId) {
            callback([]);
            return () => {};
        }

        const { userId } = this;

        const whereFilters: NonNullable<CollectionQueryConstraints["where"]> = [
            ["owner", "==", this.workspaceId]
        ];
        if (this.kinds.length) {
            whereFilters.push(["kind", "in", this.kinds]);
        }

        return this.database.onQuerySnapshot(
            "activity_events",
            { where: whereFilters, orderBy: ["createdAt", "desc"], limit: 50 },
            (docs) => {
                const activities: ActivityViewData[] = [];
                for (const doc of docs) {
                    const data = doc.data();
                    if (!data) continue;
                    const event = { ...data, id: doc.id } as FirestoreActivityEvent;

                    if (event.audience.mode === ActivityAudienceMode.Users) {
                        if (!userId || !event.audience.targets?.includes(userId)) continue;
                    } else if (event.audience.mode !== ActivityAudienceMode.All) {
                        // TODO: `Permissions` mode is reserved for a planned backend feature
                        // (targets = workspace role ids). Silently filter until it lands so
                        // future events don't leak through with an unsupported audience shape.
                        continue;
                    }

                    const converted = convertEvent(event);
                    if (converted) activities.push(converted);
                }
                callback(activities);
            },
            (error: Error) => {
                // eslint-disable-next-line no-console
                console.error("ActivityEventSource onSnapshot error:", error);
                callback([]);
            }
        );
    }
}

export { ActivityEventSource };
